import React, { useEffect, useMemo, useRef, useState } from "react";
import Container from "../components/Container.jsx";
import OutgrowEmbed from "../components/OutgrowEmbed.jsx";
import CalculatorModal from "../components/CalculatorModal.jsx";

export default function MortgageCalc() {
  const url = import.meta.env.VITE_OUTGROW_MORTGAGE_URL || "";
  const calculator = "mortgage";

  const [calcData, setCalcData] = useState(null); // { type, answers }
  const [open, setOpen] = useState(false);
  const [utms, setUtms] = useState({});
  const lastEvt = useRef(null);

  /* ---------- UTM helpers ---------- */
  function parseParams(str) {
    const out = {};
    const qs = new URLSearchParams(str || "");
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]) {
      const v = qs.get(k);
      if (v) out[k] = v;
    }
    return out;
  }
  function readUtmsFromUrl() {
    const search = window.location.search || "";
    const hash = window.location.hash || "";
    const afterHash = hash.includes("?") ? hash.split("?")[1] : "";
    return { ...parseParams(search), ...parseParams(afterHash) };
  }
  function loadPersistedUtms() {
    try { return JSON.parse(sessionStorage.getItem("utm_params") || "{}"); } catch { return {}; }
  }
  function savePersistedUtms(obj) {
    try { sessionStorage.setItem("utm_params", JSON.stringify(obj)); } catch {}
  }

  /* ---------- Hash/query helpers ---------- */
  function readHashParams() {
    const hash = window.location.hash || "";
    const qs = hash.includes("?") ? hash.split("?")[1] : "";
    return new URLSearchParams(qs);
  }

  // Re-parse whenever hash (including query after #) changes.
  useEffect(() => {
    function handleHashChange() {
      const viaUrl = parseOutgrowFromHash?.() || null;
      if (viaUrl) setCalcData(viaUrl);
      const p = readHashParams();
      if (p.get("og_open") === "1") setOpen(true);
    }
    handleHashChange();
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  /* ---------- Parse Outgrow CTA values (Q1..Qn / R1 or JSON) ---------- */
  function parseOutgrowFromHash() {
    const p = readHashParams();

    const leadId  = p.get("lead_id") || p.get("calclead") || "";
    const visitor = p.get("visitor") || p.get("visitorkey") || "";
    const result  = p.get("result") || p.get("res") || p.get("R1") || p.get("r1") || "";

    const qaRaw = p.get("qa") || p.get("questions_json") || p.get("questions") || p.get("answers") || "";
    let answers = {};

    if (qaRaw) {
      try { answers = JSON.parse(qaRaw); }
      catch { answers = { raw: qaRaw }; }
    }

    if (!qaRaw) {
      const ignore = new Set([
        "og_complete","og_open","lead_id","calclead","visitor","visitorkey",
        "result","res","R1","r1","utm","utm_source","utm_medium","utm_campaign","utm_term","utm_content"
      ]);
      for (const [k, v] of p.entries()) {
        if (ignore.has(k)) continue;
        if (/^q\d+$/i.test(k)) answers[k.toLowerCase()] = v;
        else answers[k] = v;
      }
    }

    if (leadId)  answers.lead_id = leadId;
    if (visitor) answers.visitor_key = visitor;
    if (result)  answers.result = result;

    if (Object.keys(answers).length) return { type: "completed", answers };
    return null;
  }

  /* ---------- UTM bootstrap ---------- */
  useEffect(() => {
    const fromUrl = readUtmsFromUrl();
    const stored = loadPersistedUtms();
    const merged = { ...stored, ...fromUrl };
    setUtms(merged);
    savePersistedUtms(merged);
  }, []);

  /* ---------- Optional: accept Outgrow postMessage ---------- */
  const originAllow = useMemo(
    () => [/\.outgrow\.(co|us|io|me|com)$/i, /^https?:\/\/.*outgrow/i],
    []
  );
  const isAllowedOrigin = (origin) => originAllow.some((re) => re.test(String(origin)));

  useEffect(() => {
    function onMessage(e) {
      if (!isAllowedOrigin(e.origin)) return;
      const norm = normalizeOutgrowEvent(e.data);
      if (!norm) return;

      lastEvt.current = { origin: e.origin, norm };

      const gotAnswers =
        norm.answers &&
        (Array.isArray(norm.answers) ? norm.answers.length > 0 : Object.keys(norm.answers || {}).length > 0);

      if (gotAnswers) {
        setCalcData({ type: norm.type || "completed", answers: norm.answers });
        window.dataLayer?.push?.({ event: "outgrow_complete", calculator });
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [calculator]);

  // Also support CTA-returned answers; auto-open only if og_open=1
  useEffect(() => {
    const viaUrl = parseOutgrowFromHash();
    const p = readHashParams();
    if (viaUrl) {
      setCalcData(viaUrl);
      if (p.get("og_open") === "1") setOpen(true);
    }
  }, []);

  return (
    <main className="py-10">
      <Container>
        {!url ? (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900">
            <b>Missing Outgrow URL.</b> Set <code>VITE_OUTGROW_MORTGAGE_URL</code> and redeploy.
          </div>
        ) : (
          <div className="mt-6">
            {/* Let the page scroll; give the iframe a generous height range */}
            <OutgrowEmbed
              url={url}
              title="Mortgage Calculator"
              minHeight={760}
              maxHeight={1600}
              bottomPad={48}
            />
          </div>
        )}

        <CalculatorModal
          open={open}
          onClose={() => setOpen(false)}
          calculator={calculator}
          outgrowUrl={url}
          calcData={calcData}
          utms={utms}
        />
      </Container>
    </main>
  );
}

/* ---------- Normalizer for various Outgrow message shapes ---------- */
function normalizeOutgrowEvent(raw) {
  if (!raw) return null;
  let data = raw;
  if (typeof raw === "string") {
    try { data = JSON.parse(raw); } catch { data = { message: raw }; }
  }
  const payload = data?.payload || data?.data || data;

  const type =
    payload?.type ||
    data?.type ||
    payload?.event ||
    data?.event ||
    (payload?.status === "completed" ? "completed" : undefined) ||
    "message";

  let answers =
    payload?.answers ||
    payload?.responses ||
    payload?.result ||
    payload?.results ||
    payload?.outcome ||
    payload?.summary ||
    data?.answers ||
    data?.responses ||
    null;

  if (!answers && payload?.final) {
    answers = payload.final.answers || payload.final.responses || payload.final;
  }

  if (Array.isArray(answers)) {
    const obj = {};
    answers.forEach((a, i) => {
      const key = a?.question?.text || a?.question || a?.label || a?.title || `q${i + 1}`;
      const val = a?.answer ?? a?.value ?? a?.selected ?? a?.response ?? null;
      obj[String(key)] = val;
    });
    answers = obj;
  }
  if (!answers || typeof answers !== "object") return { type, answers: {} };
  return { type, answers };
}
